import React from "react";
import { Table, Tooltip } from "antd";
import { sumBy } from "lodash";
import { useTimeSheetStore } from "../../../../config/stores";
import TableTitle from "../../../../components/common/TableTitle";
import AntdTable from "../../../../components/common/AntdTable";
import useTimeSheetGrid, {
  DAY_TYPE_STYLES,
  formatHours,
} from "./useTimeSheetGrid";

const LegendItem = ({ type, count }) => (
  <span className="d-flex align-items-center">
    <span
      style={{
        display: "inline-block",
        width: 14,
        height: 14,
        backgroundColor: DAY_TYPE_STYLES[type].backgroundColor,
        border: "1px solid #adb5bd",
        borderRadius: 2,
        marginRight: 6,
      }}
    />
    {DAY_TYPE_STYLES[type].label} ({count})
  </span>
);

export const ActivityList = ({ activities }) =>
  activities.map((activity) => (
    <div key={activity.id} className="mb-1" style={{ lineHeight: 1.35 }}>
      <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {activity.text}
      </span>{" "}
      <span className="text-muted" style={{ fontSize: "0.85em" }}>
        ({activity.columnTitle} · {formatHours(activity.hours)}h)
      </span>
    </div>
  ));

const TimeSheetActivitiesTable = ({ showHeader = false }) => {
  const timeSheetsStore = useTimeSheetStore();
  const { timeSheetDetails } = timeSheetsStore;
  const grid = useTimeSheetGrid(timeSheetDetails);

  const openAddModal = () => {
    timeSheetsStore.setAddingTimeSheetActivity(true, true, {});
  };

  const dayColumns = [
    {
      title: "DATE",
      dataIndex: "date",
      fixed: "left",
      width: 105,
      render: (date) => <span className="text-primary">{date}</span>,
    },
    {
      title: "DAY",
      dataIndex: "dayName",
      width: 90,
      render: (dayName, record) => (
        <div style={{ fontSize: "0.85em" }}>
          <div>{dayName}</div>
          {record.holidayName && (
            <div className="fw-bold">{record.holidayName}</div>
          )}
          {record.type === "leave" && <div className="fw-bold">{record.leaveName}</div>}
        </div>
      ),
    },
    {
      title: "ACTIVITIES",
      dataIndex: "activities",
      width: 420,
      render: (activities) => <ActivityList activities={activities} />,
    },
    ...grid.columns.map((column) => ({
      title: (
        <Tooltip title={column.title}>
          <span style={{ fontSize: "0.8em", wordBreak: "break-word" }}>
            {column.title}
          </span>
        </Tooltip>
      ),
      key: column.key,
      align: "center",
      width: 105,
      render: (_, record) => formatHours(record.hoursByColumn[column.key]),
    })),
    {
      title: "TOTAL",
      dataIndex: "total",
      align: "center",
      fixed: "right",
      width: 80,
      render: (total) => <span className="fw-bold">{formatHours(total)}</span>,
    },
  ];

  const columns = dayColumns.map((column) => ({
    ...column,
    onCell: (record) => ({
      style: { backgroundColor: DAY_TYPE_STYLES[record.type].backgroundColor },
    }),
  }));

  const scrollX = sumBy(columns, "width");

  const renderSummaryRow = (label, valuesByColumn, total) => (
    <Table.Summary.Row style={{ backgroundColor: "#f5f5f5" }}>
      <Table.Summary.Cell index={0} className="fw-bold">
        {label}
      </Table.Summary.Cell>
      <Table.Summary.Cell index={1} />
      <Table.Summary.Cell index={2} />
      {grid.columns.map((column, idx) => (
        <Table.Summary.Cell
          key={column.key}
          index={3 + idx}
          align="center"
          className="fw-bold"
        >
          {valuesByColumn[column.key]}
        </Table.Summary.Cell>
      ))}
      <Table.Summary.Cell
        index={3 + grid.columns.length}
        align="center"
        className="fw-bold"
      >
        {total}
      </Table.Summary.Cell>
    </Table.Summary.Row>
  );

  const totals = {};
  grid.columns.forEach(({ key }) => {
    totals[key] = formatHours(grid.totalsByColumn[key]);
  });

  return (
    <div>
      <div
        className="d-flex flex-wrap align-items-center gap-3 mb-2"
        style={{ fontSize: "0.85em" }}
      >
        <LegendItem type="weekend" count={grid.counts.weekend} />
        <LegendItem type="holiday" count={grid.counts.holiday} />
        <LegendItem type="leave" count={grid.counts.leave} />
        <span className="ms-auto text-muted">
          Days worked: <strong>{grid.workedDays}</strong> · Total hours:{" "}
          <strong>{formatHours(grid.totalHours) || 0}</strong>
        </span>
      </div>

      <AntdTable
        loading={timeSheetsStore.gettingTimeSheetDetails}
        title={
          !showHeader
            ? () => (
                <TableTitle
                  openAddModal={
                    timeSheetDetails.status === "approved" ? null : openAddModal
                  }
                  refreshTable={null}
                  search={null}
                  exportRecords={null}
                />
              )
            : null
        }
        rowKey="key"
        pagination={false}
        data={grid.rows}
        columns={columns}
        scrollX={scrollX}
        scrollY="65vh"
        sticky
        summary={() => (
          <Table.Summary fixed="bottom">
            {renderSummaryRow("TOTAL", totals, formatHours(grid.totalHours) || 0)}
            {renderSummaryRow(
              "PERCENTAGE",
              grid.percentagesByColumn,
              grid.totalHours ? "100%" : ""
            )}
          </Table.Summary>
        )}
        onRow={(record) => ({
          style: { cursor: "pointer" },
          onClick: () =>
            timeSheetsStore.setShowTimeSheetActivities(record, true),
        })}
      />
    </div>
  );
};

export default TimeSheetActivitiesTable;
